import React, { useState, useEffect } from 'react';
import moment from 'moment';
import axios from 'axios';
import 'jspdf-autotable';
import { 
  setupPDF, 
  getPageDimensions, 
  addHeader, 
  addFooter,
  addPaymentInfo,
  colors,
  fonts,
  tableStyles,
  savePDF,
  autoTable
} from '../utils/pdfTemplate';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

function Dashboard({ gigs, user, onNewGig, onSelectGig, onEditGig, onDeleteGig, onNavigateToGigs, onNavigateToProfile }) {
  const [timePreset, setTimePreset] = useState('month'); // 'all', 'year', 'month', 'week'
  const [showIbanReminder, setShowIbanReminder] = useState(false);
  const [showVatReminder, setShowVatReminder] = useState(false);
  
  // VAT Tracker state
  const [vatYear, setVatYear] = useState(new Date().getFullYear());
  const [vatSummary, setVatSummary] = useState(null);
  const [vatDates, setVatDates] = useState(null);
  const [showVatConfig, setShowVatConfig] = useState(false);
  const [vatConfigDates, setVatConfigDates] = useState({});
  const [vatLoading, setVatLoading] = useState(false);

  // Check if we should show IBAN reminder
  useEffect(() => {
    const dismissed = localStorage.getItem('iban_reminder_dismissed');
    const hasIban = user?.iban && user.iban.trim() !== '';
    // Show reminder only if user has no IBAN and hasn't dismissed the reminder
    setShowIbanReminder(!hasIban && !dismissed);
  }, [user]);

  // Check if we should show VAT reminder
  useEffect(() => {
    const dismissed = localStorage.getItem('vat_reminder_dismissed');
    const hasVatStatus = user?.vat_status && user.vat_status.trim() !== '';
    // Show reminder only if user has no VAT status and hasn't dismissed the reminder
    setShowVatReminder(!hasVatStatus && !dismissed);
  }, [user]);

  const handleDismissIbanReminder = () => {
    localStorage.setItem('iban_reminder_dismissed', 'true');
    setShowIbanReminder(false);
  };

  const handleDismissVatReminder = () => {
    localStorage.setItem('vat_reminder_dismissed', 'true');
    setShowVatReminder(false);
  };

  // Fetch VAT data when user has VAT enabled
  useEffect(() => {
    const fetchVatData = async () => {
      if (user?.vat_status !== 'regular') return;
      
      setVatLoading(true);
      try {
        const [summaryRes, datesRes] = await Promise.all([
          axios.get(`${API_BASE}/vat/summary/${vatYear}`),
          axios.get(`${API_BASE}/vat/dates/${vatYear}`)
        ]);
        setVatSummary(summaryRes.data);
        setVatDates(datesRes.data);
        setVatConfigDates(datesRes.data);
      } catch (error) {
        console.error('Error fetching VAT data:', error);
      } finally {
        setVatLoading(false);
      }
    };
    
    fetchVatData();
  }, [user?.vat_status, vatYear]);

  const handleSaveVatDates = async () => {
    try {
      const response = await axios.put(`${API_BASE}/vat/dates/${vatYear}`, vatConfigDates);
      setVatDates(response.data);
      setShowVatConfig(false);
    } catch (error) {
      console.error('Error saving VAT dates:', error);
      alert('Error saving VAT dates. Please try again.');
    }
  };

  const handleMarkVatPaid = async (trimester) => {
    try {
      const updatedDates = { ...vatDates, [`${trimester}_paid`]: 1 };
      const response = await axios.put(`${API_BASE}/vat/dates/${vatYear}`, updatedDates);
      setVatDates(response.data);
      setVatConfigDates(response.data);
    } catch (error) {
      console.error('Error marking VAT as paid:', error);
      alert('Error updating VAT status. Please try again.');
    }
  };

  const handleMarkVatUnpaid = async (trimester) => {
    try {
      const updatedDates = { ...vatDates, [`${trimester}_paid`]: 0 };
      const response = await axios.put(`${API_BASE}/vat/dates/${vatYear}`, updatedDates);
      setVatDates(response.data);
      setVatConfigDates(response.data);
    } catch (error) {
      console.error('Error marking VAT as unpaid:', error);
      alert('Error updating VAT status. Please try again.');
    }
  };

  const getDateStatus = (dateStr) => {
    if (!dateStr) return 'unknown';
    const date = moment(dateStr);
    const today = moment().startOf('day');
    if (date.isBefore(today)) return 'past';
    if (date.isSame(today, 'day')) return 'today';
    if (date.diff(today, 'days') <= 7) return 'soon';
    return 'future';
  };

  // Check if government auto VAT tool is available for a trimester
  // Available after the 15th of the month following each trimester's end
  // T1 (Jan-Mar) -> April 15, T2 (Apr-Jun) -> July 15, T3 (Jul-Sep) -> October 15, T4 (Oct-Dec) -> January 15 (next year)
  const getAutoVatInfo = (trimesterIndex, year) => {
    const today = moment().startOf('day');
    let availableDate;
    
    switch (trimesterIndex) {
      case 0: // T1: available after April 15
        availableDate = moment(`${year}-04-15`);
        break;
      case 1: // T2: available after July 15
        availableDate = moment(`${year}-07-15`);
        break;
      case 2: // T3: available after October 15
        availableDate = moment(`${year}-10-15`);
        break;
      case 3: // T4: available after January 15 of NEXT year
        availableDate = moment(`${year + 1}-01-15`);
        break;
      default:
        return { available: false, date: null };
    }
    
    return {
      available: today.isSameOrAfter(availableDate, 'day'),
      date: availableDate
    };
  };

  // Export VAT Report to PDF
  const exportVatToPDF = () => {
    if (!vatSummary || !vatSummary.trimesters) {
      alert('No VAT data available to export');
      return;
    }

    const doc = setupPDF('portrait');
    const { pageWidth, margin, contentWidth } = getPageDimensions(doc);

    // Add header
    let y = addHeader(doc, 'VAT REPORT', `VAT Summary ${vatYear}`, moment().format('D MMM YYYY'));

    // Summary box
    y += 5;
    doc.setFillColor(...colors.bgLight);
    doc.roundedRect(margin, y, contentWidth, 28, 2, 2, 'F');
    doc.setFillColor(...colors.yellow);
    doc.rect(margin, y, 3, 28, 'F');

    doc.setFontSize(fonts.label);
    doc.setTextColor(...colors.lightGray);
    doc.text('TOTAL VAT DUE', margin + 10, y + 8);

    doc.setFontSize(16);
    doc.setTextColor(...colors.darkGray);
    doc.setFont('helvetica', 'bold');
    doc.text(`€${vatSummary.totalVat?.toFixed(2) || '0.00'}`, margin + 10, y + 20);
    doc.setFont('helvetica', 'normal');

    // Paid vs Unpaid summary
    const paidTrimesters = ['t1', 't2', 't3', 't4'].filter(t => 
      vatDates?.[`${t}_paid`] === 1 || vatDates?.[`${t}_paid`] === true
    ).length;
    doc.setFontSize(fonts.body);
    doc.setTextColor(...colors.lightGray);
    doc.text(`${paidTrimesters}/4 Trimesters Paid`, pageWidth - margin - 10, y + 14, { align: 'right' });

    y += 38;

    // Table data
    const tableData = vatSummary.trimesters.map((t, idx) => {
      const autoVatInfo = getAutoVatInfo(idx, vatYear);
      const declDate = vatDates?.[`t${idx + 1}_declaration`];
      const payDate = vatDates?.[`t${idx + 1}_payment`];
      const isPaid = vatDates?.[`t${idx + 1}_paid`] === 1 || vatDates?.[`t${idx + 1}_paid`] === true;

      return [
        t.trimester,
        t.period,
        autoVatInfo.available ? '✓ ' + autoVatInfo.date?.format('MMM D') : '✗ ' + autoVatInfo.date?.format('MMM D'),
        t.vatAmount > 0 ? `€${t.vatAmount.toFixed(2)}` : '-',
        declDate ? moment(declDate).format('MMM D, YYYY') : '-',
        payDate ? moment(payDate).format('MMM D, YYYY') : '-',
        isPaid ? '✓ Paid' : 'Unpaid'
      ];
    });

    // Generate table
    autoTable(doc, {
      startY: y,
      head: [['Trimester', 'Period', 'Auto VAT', 'VAT Amount', 'Declaration', 'Payment', 'Status']],
      body: tableData,
      margin: { left: margin, right: margin },
      headStyles: {
        ...tableStyles.head,
        halign: 'center'
      },
      bodyStyles: tableStyles.body,
      styles: tableStyles.styles,
      columnStyles: {
        0: { halign: 'center', fontStyle: 'bold' },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'right', fontStyle: 'bold' },
        4: { halign: 'center' },
        5: { halign: 'center' },
        6: { halign: 'center' }
      },
      didParseCell: (data) => {
        // Color the Auto VAT and Status columns
        if (data.section === 'body') {
          if (data.column.index === 2) {
            // Auto VAT column
            const text = data.cell.raw;
            if (text.startsWith('✓')) {
              data.cell.styles.textColor = colors.green;
            } else {
              data.cell.styles.textColor = [220, 53, 69]; // red
            }
          }
          if (data.column.index === 6) {
            // Status column
            const text = data.cell.raw;
            if (text === '✓ Paid') {
              data.cell.styles.textColor = colors.green;
            } else {
              data.cell.styles.textColor = colors.orange;
            }
          }
        }
      },
      alternateRowStyles: tableStyles.alternateRow
    });

    y = doc.lastAutoTable.finalY + 15;

    // Add trimester breakdown if there's space
    if (y < 200) {
      doc.setFontSize(fonts.heading);
      doc.setTextColor(...colors.darkGray);
      doc.setFont('helvetica', 'bold');
      doc.text('Trimester Periods', margin, y);
      doc.setFont('helvetica', 'normal');
      y += 8;

      doc.setFontSize(fonts.body);
      doc.setTextColor(...colors.mediumGray);
      const periods = [
        'T1: January - March',
        'T2: April - June', 
        'T3: July - September',
        'T4: October - December'
      ];
      periods.forEach((period, idx) => {
        const autoInfo = getAutoVatInfo(idx, vatYear);
        doc.text(`${period} → Auto VAT from ${autoInfo.date?.format('MMM D, YYYY')}`, margin, y);
        y += 5;
      });
    }

    // Add IBAN if available
    addPaymentInfo(doc, user?.iban);

    // Add footer
    addFooter(doc, { username: user?.username || 'Unknown' });

    // Save the PDF
    savePDF(doc, `vat-report-${vatYear}.pdf`);
  };

  // Get date range based on preset
  const getDateRange = () => {
    switch (timePreset) {
      case 'all':
        return { start: null, end: null };
      case 'year':
        return {
          start: moment().startOf('year'),
          end: moment().endOf('year')
        };
      case 'month':
        return {
          start: moment().startOf('month'),
          end: moment().endOf('month')
        };
      case 'week':
        return {
          start: moment().startOf('week'),
          end: moment().endOf('week')
        };
      default:
        return {
          start: moment().startOf('year'),
          end: moment().endOf('year')
        };
    }
  };

  const { start, end } = getDateRange();

  // Filter gigs based on selected time preset
  const filteredGigs = start && end 
    ? gigs.filter(gig => {
        if (!gig.start_date) return false;
        const gigDate = moment(gig.start_date);
        if (!gigDate.isValid()) return false;
        // Normalize dates to start of day for accurate comparison
        const gigDateStart = gigDate.clone().startOf('day');
        const rangeStart = start.clone().startOf('day');
        const rangeEnd = end.clone().startOf('day');
        // Check if gig's start_date falls within the range (inclusive)
        const isInRange = gigDateStart.isSameOrAfter(rangeStart, 'day') && gigDateStart.isSameOrBefore(rangeEnd, 'day');
        return isInRange;
      })
    : gigs; // All time

  // Get display label for current preset
  const getPresetLabel = () => {
    switch (timePreset) {
      case 'all':
        return 'All Time';
      case 'year':
        return moment().format('YYYY');
      case 'month':
        return moment().format('MMMM YYYY');
      case 'week':
        const weekStart = moment().startOf('week');
        const weekEnd = moment().endOf('week');
        return `${weekStart.format('MMM D')} - ${weekEnd.format('MMM D, YYYY')}`;
      default:
        return moment().format('YYYY');
    }
  };

  // Calculate number of gigs
  const numberOfGigs = filteredGigs.length;

  // Calculate amount to collect (total profit from unpaid/partially paid gigs, without substitute payments)
  // This represents how much money is pending to receive from unpaid gigs
  // Always show total regardless of time filter - only includes gigs that have ENDED
  const today = moment().startOf('day');
  const amountToCollect = gigs
    .filter(gig => {
      const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
      // Only count non-canceled gigs that have ended (end_date has passed or is today)
      if (!gig.end_date || isCanceled) return false;
      const gigEndDate = moment(gig.end_date);
      if (!gigEndDate.isValid()) return false;
      const gigEndDateStart = gigEndDate.clone().startOf('day');
      const hasEnded = gigEndDateStart.isSameOrBefore(today, 'day');
      if (!hasEnded) return false;
      
      // Calculate revenue and paid amount
      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
      const rateType = gig.rate_type || 'daily';
      const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
      const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
      const revenue = baseRevenue + additionalCharges;
      const paidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
      const isPaid = gig.paid === 1 || gig.paid === true || paidAmount >= revenue;
      
      // Include gigs that are unpaid or partially paid
      return !isPaid;
    })
    .reduce((total, gig) => {
      // Calculate revenue
      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
      const rateType = gig.rate_type || 'daily';
      const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
      const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
      const revenue = baseRevenue + additionalCharges;
      const paidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
      
      // Calculate expenses (only self-covered expenses, as company-covered are reimbursed)
      const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self')
        .reduce((expSum, exp) => expSum + parseFloat(exp.amount), 0) || 0;
      
      // Outstanding amount = Revenue - Paid Amount - Self Expenses (no substitute payments deducted)
      const outstanding = revenue - paidAmount - selfExpenses;
      return total + outstanding;
    }, 0);

  // Calculate unpaid/partially paid gigs (all gigs, not just filtered)
  // Only include gigs that have ended (end_date has passed)
  const unpaidGigs = gigs.filter(gig => {
    const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
    const hasEnded = gig.end_date && moment(gig.end_date).startOf('day').isBefore(today);
    if (isCanceled || !hasEnded) return false;
    
    // Calculate if gig is unpaid or partially paid
    const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
    const rateType = gig.rate_type || 'daily';
    const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
    const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
    const revenue = baseRevenue + additionalCharges;
    const paidAmount = gig.paid_amount !== undefined && gig.paid_amount !== null ? parseFloat(gig.paid_amount) : 0;
    const isPaid = gig.paid === 1 || gig.paid === true || paidAmount >= revenue;
    
    // Include gigs that are unpaid or partially paid
    return !isPaid;
  });

  // Calculate unpaid substitutes (all gigs, not just filtered)
  // Only include gigs that have ended (end_date has passed)
  const unpaidSubs = gigs.filter(gig => {
    const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
    const substitutePaid = gig.substitute_paid === 1 || gig.substitute_paid === true;
    const substituteNotPaid = gig.substitute_not_paid === 1 || gig.substitute_not_paid === true;
    const hasEnded = gig.end_date && moment(gig.end_date).startOf('day').isBefore(today);
    // Include sub_for_me gigs that have ended
    // Exclude if substitute has been paid or if explicitly marked as "substitute_not_paid"
    return gigType === 'sub_for_me' && !substitutePaid && !substituteNotPaid && hasEnded;
  });

  // Calculate upcoming gigs this week (from today to end of week)
  const weekEnd = moment().endOf('week').startOf('day');
  
  const upcomingThisWeek = gigs
    .filter(gig => {
      if (!gig.start_date) return false;
      try {
        const gigDate = moment(gig.start_date).startOf('day');
        if (!gigDate.isValid()) {
          console.warn('Invalid date for gig:', gig.id, gig.start_date);
          return false;
        }
        const isCanceled = gig.is_canceled === 1 || gig.is_canceled === true;
        // Include gigs that start today or later, and are within this week (including today)
        const isUpcoming = gigDate.isSameOrAfter(today, 'day');
        const isThisWeek = gigDate.isSameOrBefore(weekEnd, 'day');
        return isUpcoming && isThisWeek && !isCanceled;
      } catch (error) {
        console.error('Error parsing gig date:', gig.start_date, error);
        return false;
      }
    })
    .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)))
    .slice(0, 5); // Show up to 5 upcoming gigs
  
  // Debug log (remove in production)
  console.log('Upcoming this week:', upcomingThisWeek.length, 'gigs found');
  console.log('Today:', today.format('YYYY-MM-DD'), 'Week end:', weekEnd.format('YYYY-MM-DD'));

  // Calculate total money earned (total revenue) - only count gigs that have ended
  const moneyEarned = filteredGigs
    .filter(gig => {
      // Only count gigs that have ended (end_date has passed or is today)
      if (!gig.end_date) return false;
      const gigEndDate = moment(gig.end_date);
      if (!gigEndDate.isValid()) return false;
      const gigEndDateStart = gigEndDate.clone().startOf('day');
      const hasEnded = gigEndDateStart.isSameOrBefore(today, 'day');
      return hasEnded;
    })
    .reduce((sum, gig) => {
      const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
      const rateType = gig.rate_type || 'daily';
      const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
      const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
      const revenue = baseRevenue + additionalCharges;
      return sum + revenue;
    }, 0);

  // Calculate total money spent (self-covered expenses) - filtered by time period
  const moneySpent = filteredGigs.reduce((sum, gig) => {
    const selfExpenses = gig.expenses?.filter(exp => exp.covered_by === 'self')
      .reduce((expSum, exp) => expSum + parseFloat(exp.amount), 0) || 0;
    return sum + selfExpenses;
  }, 0);

  // Calculate outstanding balance to subs (unpaid substitutes where someone subbed for me)
  // Only include gigs where substitute has NOT been paid
  const outstandingBalanceToSubs = gigs.reduce((sum, gig) => {
    const gigType = gig.gig_type || (gig.is_substitution === 1 || gig.is_substitution === true ? 'sub_for_other' : 'mine');
    const substitutePaid = gig.substitute_paid === 1 || gig.substitute_paid === true;
    if (gigType === 'sub_for_me' && !substitutePaid && gig.substitute_amount) {
      return sum + parseFloat(gig.substitute_amount);
    }
    return sum;
  }, 0);

  // Calculate net profit (earned - spent - outstanding to subs)
  const netProfit = moneyEarned - moneySpent - outstandingBalanceToSubs;

  // Calculate most visited locations
  const locationCounts = {};
  filteredGigs.forEach(gig => {
    if (gig.venue && gig.venue.trim()) {
      locationCounts[gig.venue] = (locationCounts[gig.venue] || 0) + 1;
    }
  });
  const topLocations = Object.entries(locationCounts)
    .map(([location, count]) => ({ location, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  // Calculate top working companies
  const companyData = {};
  filteredGigs.forEach(gig => {
    const companyName = gig.client_name || 'Unknown Client';
    if (!companyData[companyName]) {
      companyData[companyName] = {
        name: companyName,
        gigCount: 0,
        totalRevenue: 0
      };
    }
    companyData[companyName].gigCount += 1;
    const days = moment(gig.end_date).diff(moment(gig.start_date), 'days') + 1;
    const rateType = gig.rate_type || 'daily';
    const baseRevenue = rateType === 'flat' ? parseFloat(gig.daily_rate) : days * parseFloat(gig.daily_rate);
    const additionalCharges = gig.additional_charges?.reduce((sum, charge) => sum + parseFloat(charge.amount), 0) || 0;
    const revenue = baseRevenue + additionalCharges;
    companyData[companyName].totalRevenue += revenue;
  });
  const topCompanies = Object.values(companyData)
    .sort((a, b) => b.totalRevenue - a.totalRevenue)
    .slice(0, 5);

  const getGigsLabel = () => {
    switch (timePreset) {
      case 'all':
        return 'Total Gigs';
      case 'year':
        return 'Gigs This Year';
      case 'month':
        return 'Gigs This Month';
      case 'week':
        return 'Gigs This Week';
      default:
        return 'Gigs';
    }
  };

  const stats = [
    {
      label: getGigsLabel(),
      value: numberOfGigs,
      icon: '📅',
      color: '#ffd700'
    },
    {
      label: 'Money Earned',
      value: `€${moneyEarned.toFixed(2)}`,
      icon: '💶',
      color: '#4caf50'
    },
    {
      label: 'Self Covered Expenses',
      value: `€${moneySpent.toFixed(2)}`,
      icon: '💸',
      color: '#ff6b6b'
    },
    {
      label: 'Outstanding to Subs',
      value: `€${outstandingBalanceToSubs.toFixed(2)}`,
      icon: '💰',
      color: '#ff9800'
    },
    {
      label: 'Net Profit',
      value: `€${netProfit.toFixed(2)}`,
      icon: '💵',
      color: netProfit >= 0 ? '#4caf50' : '#ff6b6b'
    },
    {
      label: 'Amount to Collect',
      value: `€${amountToCollect.toFixed(2)}`,
      icon: '💰',
      color: '#ffd700'
    }
  ];

  return (
    <div className="dashboard">
      {/* IBAN Reminder Banner */}
      {showIbanReminder && (
        <div style={{
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
          border: '1px solid #d4a017',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '200px' }}>
            <span style={{ fontSize: '1.5rem' }}>🏦</span>
            <div>
              <div style={{ color: '#e5e5e5', fontWeight: 600, marginBottom: '0.25rem' }}>
                Add your IBAN for easier payments
              </div>
              <div style={{ color: '#999', fontSize: '0.85rem' }}>
                Your IBAN will appear on exported PDFs so clients can pay you directly.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={onNavigateToProfile}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Add IBAN
            </button>
            <button
              onClick={handleDismissIbanReminder}
              className="btn btn-secondary"
              style={{ 
                padding: '0.5rem 0.75rem',
                minWidth: 'auto',
                opacity: 0.7
              }}
              title="Dismiss this reminder"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* VAT Reminder Banner */}
      {showVatReminder && (
        <div style={{
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
          border: '1px solid #d4a017',
          borderRadius: '8px',
          padding: '1rem 1.25rem',
          marginBottom: '1.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: '200px' }}>
            <span style={{ fontSize: '1.5rem' }}>📋</span>
            <div>
              <div style={{ color: '#e5e5e5', fontWeight: 600, marginBottom: '0.25rem' }}>
                Configure your VAT status
              </div>
              <div style={{ color: '#999', fontSize: '0.85rem' }}>
                Set whether you're VAT Exempt or charge Regular VAT (23%) on your invoices.
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={onNavigateToProfile}
              className="btn btn-primary"
              style={{ whiteSpace: 'nowrap' }}
            >
              Configure VAT
            </button>
            <button
              onClick={handleDismissVatReminder}
              className="btn btn-secondary"
              style={{ 
                padding: '0.5rem 0.75rem',
                minWidth: 'auto',
                opacity: 0.7
              }}
              title="Dismiss this reminder"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1 }}>
          <div>
            <h1>Dashboard {getPresetLabel()}</h1>
            <p className="dashboard-subtitle">Overview of your freelance activity</p>
          </div>
          <div className="dashboard-presets">
            <button
              className={`preset-btn ${timePreset === 'all' ? 'active' : ''}`}
              onClick={() => setTimePreset('all')}
            >
              All Time
            </button>
            <button
              className={`preset-btn ${timePreset === 'year' ? 'active' : ''}`}
              onClick={() => setTimePreset('year')}
            >
              Year
            </button>
            <button
              className={`preset-btn ${timePreset === 'month' ? 'active' : ''}`}
              onClick={() => setTimePreset('month')}
            >
              Month
            </button>
            <button
              className={`preset-btn ${timePreset === 'week' ? 'active' : ''}`}
              onClick={() => setTimePreset('week')}
            >
              Week
            </button>
          </div>
        </div>
        {onNewGig && (
          <button 
            className="btn btn-primary"
            onClick={() => {
              onNewGig();
            }}
          >
            + New Gig
          </button>
        )}
      </div>

      <div className="dashboard-stats">
        {stats.map((stat, index) => (
          <div key={index} className="dashboard-stat-card">
            <div className="stat-icon" style={{ color: stat.color }}>
              {stat.icon}
            </div>
            <div className="stat-content">
              <div className="stat-label">{stat.label}</div>
              <div className="stat-value" style={{ color: stat.color }}>
                {stat.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Upcoming Gigs This Week Widget */}
      <div className="dashboard-widgets">
        <div className="dashboard-widget">
          <div className="widget-header">
            <span className="widget-icon">📅</span>
            <h2 className="widget-title">Upcoming - {moment().format('MMM D, YYYY')} {upcomingThisWeek && upcomingThisWeek.length > 0 && `(${upcomingThisWeek.length})`}</h2>
          </div>
          <div className="widget-content">
            {upcomingThisWeek && upcomingThisWeek.length > 0 ? (
              <ul className="widget-list">
                {upcomingThisWeek.map((gig) => (
                  <li 
                    key={gig.id} 
                    className="widget-list-item"
                    style={{ cursor: onSelectGig ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (onSelectGig) {
                        onSelectGig(gig);
                        if (onNavigateToGigs) {
                          onNavigateToGigs();
                        }
                      }
                    }}
                  >
                    <div className="widget-item-main">
                      <span className="widget-item-label">{gig.title}</span>
                      <span className="widget-item-value">
                        {moment(gig.start_date).format('MMM D')}
                      </span>
                    </div>
                    <div className="widget-item-secondary">
                      <span className="widget-item-subtext">
                        {gig.client_name || 'No Client'} • {gig.venue || 'No Venue'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div style={{ padding: '1rem', textAlign: 'center', color: '#999', fontSize: '0.9rem' }}>
                No upcoming gigs this week
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unpaid Gigs and Unpaid Subs Widgets */}
      <div className="dashboard-widgets">
        {unpaidGigs.length > 0 && (
          <div className="dashboard-widget">
            <div 
              className="widget-header" 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: onNavigateToGigs ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (onNavigateToGigs) {
                  // Filter for unpaid gigs, excluding canceled
                  onNavigateToGigs({ filterPaid: 'unpaid' });
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="widget-icon">💸</span>
                <h2 className="widget-title">Unpaid Gigs ({unpaidGigs.length})</h2>
              </div>
              {onNavigateToGigs && (
                <button
                  className="btn btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateToGigs({ filterPaid: 'unpaid' });
                  }}
                  style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                >
                  View All →
                </button>
              )}
            </div>
            <div className="widget-content">
              <ul className="widget-list">
                {unpaidGigs.slice(0, 5).map((gig) => (
                  <li 
                    key={gig.id} 
                    className="widget-list-item"
                    style={{ cursor: onSelectGig ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (onSelectGig) {
                        onSelectGig(gig);
                        if (onNavigateToGigs) {
                          onNavigateToGigs({ filterPaid: 'unpaid' });
                        }
                      }
                    }}
                  >
                    <div className="widget-item-main">
                      <span className="widget-item-label">{gig.title}</span>
                      <span className="widget-item-value">
                        {moment(gig.start_date).format('MMM D, YYYY')}
                      </span>
                    </div>
                    <div className="widget-item-secondary">
                      <span className="widget-item-subtext">
                        {gig.client_name || 'No Client'} • {gig.venue || 'No Venue'}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              {unpaidGigs.length > 5 && (
                <div style={{ padding: '0.75rem', textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                  + {unpaidGigs.length - 5} more unpaid gigs
                </div>
              )}
            </div>
          </div>
        )}

        {unpaidSubs.length > 0 && (
          <div className="dashboard-widget">
            <div 
              className="widget-header" 
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                cursor: onNavigateToGigs ? 'pointer' : 'default'
              }}
              onClick={() => {
                if (onNavigateToGigs) {
                  // Filter for unpaid subs (sub_for_me gigs that are unpaid)
                  onNavigateToGigs({ filterPaid: 'unpaid', filterGigType: 'sub_for_me' });
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="widget-icon">💰</span>
                <h2 className="widget-title">Unpaid Substitutes ({unpaidSubs.length})</h2>
              </div>
              {onNavigateToGigs && (
                <button
                  className="btn btn-secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    onNavigateToGigs({ filterPaid: 'unpaid', filterGigType: 'sub_for_me' });
                  }}
                  style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
                >
                  View All →
                </button>
              )}
            </div>
            <div className="widget-content">
              <ul className="widget-list">
                {unpaidSubs.slice(0, 5).map((gig) => (
                  <li 
                    key={gig.id} 
                    className="widget-list-item"
                    style={{ cursor: onSelectGig ? 'pointer' : 'default' }}
                    onClick={() => {
                      if (onSelectGig) {
                        onSelectGig(gig);
                        if (onNavigateToGigs) {
                          onNavigateToGigs({ filterPaid: 'unpaid', filterGigType: 'sub_for_me' });
                        }
                      }
                    }}
                  >
                    <div className="widget-item-main">
                      <span className="widget-item-label">{gig.title}</span>
                      <span className="widget-item-value">
                        €{gig.substitute_amount ? parseFloat(gig.substitute_amount).toFixed(2) : '0.00'}
                      </span>
                    </div>
                    <div className="widget-item-secondary">
                      <span className="widget-item-subtext">
                        {gig.substitute_name || 'No Name'} • {moment(gig.start_date).format('MMM D, YYYY')}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
              {unpaidSubs.length > 5 && (
                <div style={{ padding: '0.75rem', textAlign: 'center', color: '#999', fontSize: '0.85rem' }}>
                  + {unpaidSubs.length - 5} more unpaid substitutes
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* VAT Tracker Section - Only for users with regular VAT status */}
      {user?.vat_status === 'regular' && (
        <div className="vat-tracker-section" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
          <div className="vat-tracker-header" style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h2 style={{ margin: 0, color: '#e5e5e5' }}>VAT Tracker {vatYear}</h2>
              <select 
                value={vatYear} 
                onChange={(e) => setVatYear(parseInt(e.target.value))}
                style={{
                  padding: '0.35rem 0.75rem',
                  borderRadius: '4px',
                  border: '1px solid #555',
                  background: '#2a2a2a',
                  color: '#e5e5e5',
                  cursor: 'pointer'
                }}
              >
                {[...Array(5)].map((_, i) => {
                  const year = new Date().getFullYear() - 2 + i;
                  return <option key={year} value={year}>{year}</option>;
                })}
              </select>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button 
                className="btn btn-secondary"
                onClick={exportVatToPDF}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                title="Export VAT Report as PDF"
              >
                <span>📄</span> Export PDF
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowVatConfig(true)}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
              >
                <span>⚙️</span> Configure Dates
              </button>
            </div>
          </div>

          {vatLoading ? (
            <div style={{ color: '#999', padding: '2rem', textAlign: 'center' }}>Loading VAT data...</div>
          ) : vatSummary ? (
            <div className="vat-tracker-content" style={{
              background: '#1a1a1a',
              borderRadius: '8px',
              border: '1px solid #333',
              overflow: 'hidden'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} className="vat-table">
                <thead>
                  <tr style={{ background: '#2a2a2a' }}>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#d4a017', fontWeight: 600 }}>T</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right', color: '#d4a017', fontWeight: 600 }}>VAT</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#d4a017', fontWeight: 600 }} title="Auto VAT available">Auto</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#d4a017', fontWeight: 600 }}>Decl</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#d4a017', fontWeight: 600 }}>Pay</th>
                    <th style={{ padding: '0.5rem', textAlign: 'center', color: '#d4a017', fontWeight: 600 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {vatSummary.trimesters?.map((t, idx) => {
                    const declDate = vatDates?.[`t${idx + 1}_declaration`];
                    const payDate = vatDates?.[`t${idx + 1}_payment`];
                    const isPaid = vatDates?.[`t${idx + 1}_paid`] === 1 || vatDates?.[`t${idx + 1}_paid`] === true;
                    const declStatus = getDateStatus(declDate);
                    const payStatus = getDateStatus(payDate);
                    const autoVatInfo = getAutoVatInfo(idx, vatYear);
                    
                    return (
                      <tr key={t.trimester} style={{ borderTop: '1px solid #333', opacity: isPaid ? 0.6 : 1 }}>
                        <td style={{ padding: '0.5rem', color: '#e5e5e5', fontWeight: 600, textAlign: 'center' }}>
                          T{idx + 1}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'right', color: t.vatAmount > 0 ? '#4caf50' : '#666', fontWeight: 600 }}>
                          {t.vatAmount > 0 ? `€${t.vatAmount.toFixed(0)}` : '-'}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }} title={autoVatInfo.available ? 'Auto VAT available' : `Available ${autoVatInfo.date?.format('D/M')}`}>
                          {autoVatInfo.available ? (
                            <span style={{ color: '#4caf50' }}>✓</span>
                          ) : (
                            <span style={{ color: '#dc3545', fontSize: '0.75rem' }}>{autoVatInfo.date?.format('D/M')}</span>
                          )}
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <span style={{
                            fontSize: '0.8rem',
                            color: declStatus === 'past' ? '#4caf50' : declStatus === 'soon' ? '#ff9800' : '#777'
                          }}>
                            {declDate ? moment(declDate).format('D/M') : '-'}
                            {declStatus === 'past' && '✓'}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          <span style={{
                            fontSize: '0.8rem',
                            color: payStatus === 'past' ? '#4caf50' : payStatus === 'soon' ? '#ff9800' : '#777'
                          }}>
                            {payDate ? moment(payDate).format('D/M') : '-'}
                            {payStatus === 'past' && '✓'}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                          {isPaid ? (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMarkVatUnpaid(`t${idx + 1}`); }}
                              style={{
                                padding: '0.4rem 0.6rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                background: '#22863a',
                                color: '#fff',
                                fontWeight: 600,
                                border: 'none',
                                cursor: 'pointer',
                                minWidth: '44px',
                                minHeight: '32px'
                              }}
                            >
                              ✓
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleMarkVatPaid(`t${idx + 1}`); }}
                              style={{
                                padding: '0.4rem 0.6rem',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                background: '#444',
                                border: 'none',
                                color: '#ccc',
                                cursor: 'pointer',
                                minWidth: '44px',
                                minHeight: '32px'
                              }}
                            >
                              Pay
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #d4a017', background: '#2a2a2a' }}>
                    <td colSpan={3} style={{ padding: '0.75rem 1rem', color: '#e5e5e5', fontWeight: 600 }}>Total VAT ({vatYear})</td>
                    <td style={{ padding: '0.75rem 1rem', textAlign: 'right', color: '#d4a017', fontWeight: 700, fontSize: '1.1rem' }}>
                      €{vatSummary.totalVat?.toFixed(2) || '0.00'}
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div style={{ color: '#999', padding: '2rem', textAlign: 'center' }}>No VAT data available</div>
          )}

          {/* Next VAT Deadline */}
          {vatDates && (() => {
            const today = moment().startOf('day');
            
            // Find the next unpaid trimester
            const nextTrimester = ['t1', 't2', 't3', 't4'].find((t, idx) => {
              const payDate = vatDates[`${t}_payment`];
              const isPaid = vatDates[`${t}_paid`] === 1 || vatDates[`${t}_paid`] === true;
              // Show if not paid and payment date is in the future or today
              return !isPaid && payDate && moment(payDate).isSameOrAfter(today, 'day');
            });
            
            if (!nextTrimester) return null;
            
            const idx = parseInt(nextTrimester.slice(1)) - 1;
            const declDate = vatDates[`${nextTrimester}_declaration`];
            const payDate = vatDates[`${nextTrimester}_payment`];
            const declStatus = getDateStatus(declDate);
            const payStatus = getDateStatus(payDate);
            const trimesterData = vatSummary?.trimesters?.[idx];
            
            return (
              <div style={{ 
                marginTop: '1rem', 
                background: '#2a2a2a', 
                borderRadius: '8px', 
                padding: '1rem',
                border: payStatus === 'soon' ? '1px solid #dc3545' : '1px solid #444'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
                  <div>
                    <div style={{ color: '#d4a017', fontWeight: 600, marginBottom: '0.5rem' }}>
                      Next: {nextTrimester.toUpperCase()} ({trimesterData?.period || ''})
                    </div>
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                      <div>
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>Declaration: </span>
                        <span style={{ color: declStatus === 'past' ? '#4caf50' : declStatus === 'soon' ? '#ff9800' : '#e5e5e5' }}>
                          {declDate ? moment(declDate).format('MMM D') : '-'}
                          {declStatus === 'past' && ' ✓'}
                          {declStatus === 'soon' && ` (${moment(declDate).fromNow()})`}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>Payment: </span>
                        <span style={{ color: payStatus === 'soon' ? '#ff6b6b' : '#e5e5e5' }}>
                          {payDate ? moment(payDate).format('MMM D') : '-'}
                          {payStatus === 'soon' && ` (${moment(payDate).fromNow()})`}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: '#999', fontSize: '0.85rem' }}>VAT Due: </span>
                        <span style={{ color: '#d4a017', fontWeight: 600 }}>
                          €{trimesterData?.vatAmount?.toFixed(2) || '0.00'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="btn btn-success"
                    onClick={() => handleMarkVatPaid(nextTrimester)}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    ✓ Mark as Paid
                  </button>
                </div>
              </div>
            );
          })()}

          {/* VAT Config Modal */}
          {showVatConfig && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: '#1f1f1f',
                borderRadius: '12px',
                padding: '2rem',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflow: 'auto',
                border: '1px solid #333'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                  <h3 style={{ margin: 0, color: '#e5e5e5' }}>Configure VAT Dates for {vatYear}</h3>
                  <button 
                    onClick={() => setShowVatConfig(false)}
                    style={{ background: 'none', border: 'none', color: '#999', fontSize: '1.5rem', cursor: 'pointer' }}
                  >
                    ✕
                  </button>
                </div>
                
                <div style={{ display: 'grid', gap: '1.5rem' }}>
                  {['T1', 'T2', 'T3', 'T4'].map((t, idx) => (
                    <div key={t} style={{ 
                      background: '#2a2a2a', 
                      padding: '1rem', 
                      borderRadius: '8px',
                      border: '1px solid #444'
                    }}>
                      <div style={{ color: '#d4a017', fontWeight: 600, marginBottom: '0.75rem' }}>{t}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', color: '#999', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                            Declaration Date
                          </label>
                          <input
                            type="date"
                            value={vatConfigDates[`t${idx + 1}_declaration`] || ''}
                            onChange={(e) => setVatConfigDates(prev => ({ ...prev, [`t${idx + 1}_declaration`]: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #555',
                              background: '#1a1a1a',
                              color: '#e5e5e5'
                            }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', color: '#999', fontSize: '0.85rem', marginBottom: '0.35rem' }}>
                            Payment Date
                          </label>
                          <input
                            type="date"
                            value={vatConfigDates[`t${idx + 1}_payment`] || ''}
                            onChange={(e) => setVatConfigDates(prev => ({ ...prev, [`t${idx + 1}_payment`]: e.target.value }))}
                            style={{
                              width: '100%',
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid #555',
                              background: '#1a1a1a',
                              color: '#e5e5e5'
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', justifyContent: 'flex-end' }}>
                  <button className="btn btn-secondary" onClick={() => setShowVatConfig(false)}>
                    Cancel
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveVatDates}>
                    Save Dates
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* IBAN Widget - Temporarily hidden for beta testing */}
      {/* <div className="dashboard-widget" style={{ marginBottom: '2rem' }}>
        <div className="widget-header">
          <span className="widget-icon">🏦</span>
          <h2 className="widget-title">IBAN (For Your Convenience)</h2>
        </div>
        <div className="widget-content">
          <div style={{ padding: '1rem 0' }}>
            <small style={{ color: '#999', fontSize: '0.85rem', display: 'block', marginBottom: '1rem' }}>
              This field is just a place to store your IBAN if you want to have it ready for copying. 
              It is <strong>not used by the app</strong> and is <strong>not part of any data collection</strong>.
              To edit your IBAN, go to your User Profile page (click "Welcome, [username]" in the header).
            </small>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <div style={{ 
                flex: 1, 
                padding: '0.75rem', 
                background: '#1a1a1a', 
                border: '1px solid #444', 
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '1rem',
                color: iban ? '#e5e5e5' : '#666',
                minHeight: '44px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {iban || 'No IBAN stored'}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (iban) {
                    navigator.clipboard.writeText(iban).then(() => {
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    }).catch(() => {
                      // Fallback for older browsers
                      const textArea = document.createElement('textarea');
                      textArea.value = iban;
                      document.body.appendChild(textArea);
                      textArea.select();
                      document.execCommand('copy');
                      document.body.removeChild(textArea);
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    });
                  }
                }}
                className="btn btn-secondary"
                style={{ whiteSpace: 'nowrap', minWidth: '100px' }}
                disabled={!iban}
              >
                {copySuccess ? '✓ Copied!' : 'Copy IBAN'}
              </button>
            </div>
          </div>
        </div>
      </div> */}

      {numberOfGigs === 0 ? (
        <div className="dashboard-empty">
          <p>No gigs recorded for {getPresetLabel().toLowerCase()} yet. Start tracking your gigs to see your stats!</p>
        </div>
      ) : (
        <div className="dashboard-widgets">
          {topLocations.length > 0 && (
            <div className="dashboard-widget">
              <div className="widget-header">
                <span className="widget-icon">📍</span>
                <h2 className="widget-title">Most Visited Locations</h2>
              </div>
              <div className="widget-content">
                <ul className="widget-list">
                  {topLocations.map((item, index) => (
                    <li key={index} className="widget-list-item">
                      <div className="widget-item-main">
                        <span className="widget-item-label">{item.location}</span>
                        <span className="widget-item-value">{item.count} {item.count === 1 ? 'gig' : 'gigs'}</span>
                      </div>
                      <div className="widget-item-bar-container">
                        <div 
                          className="widget-item-bar" 
                          style={{ 
                            width: `${(item.count / topLocations[0].count) * 100}%`,
                            backgroundColor: '#ffd700'
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {topCompanies.length > 0 && (
            <div className="dashboard-widget">
              <div className="widget-header">
                <span className="widget-icon">👥</span>
                <h2 className="widget-title">Top Working Companies</h2>
              </div>
              <div className="widget-content">
                <ul className="widget-list">
                  {topCompanies.map((company, index) => (
                    <li key={index} className="widget-list-item">
                      <div className="widget-item-main">
                        <span className="widget-item-label">{company.name}</span>
                        <span className="widget-item-value">€{company.totalRevenue.toFixed(2)}</span>
                      </div>
                      <div className="widget-item-secondary">
                        <span className="widget-item-subtext">{company.gigCount} {company.gigCount === 1 ? 'gig' : 'gigs'}</span>
                      </div>
                      <div className="widget-item-bar-container">
                        <div 
                          className="widget-item-bar" 
                          style={{ 
                            width: `${(company.totalRevenue / topCompanies[0].totalRevenue) * 100}%`,
                            backgroundColor: '#4caf50'
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Dashboard;

